<template v-on:change="$emit('change', $event.target.checked)">
  <div>
    <div id="map" class="map-canvas-2"></div>
  </div>
</template>

<script>
import GoogleMapsLoader from "google-maps";
GoogleMapsLoader.KEY = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
GoogleMapsLoader.LIBRARIES.push("places");
GoogleMapsLoader.WINDOW_CALLBACK_NAME = "initAutocomplete";
GoogleMapsLoader.VERSION = 3.43;

export default {
  name: "GoogleMaps",
  data() {
    return {
      google: null,
      map: null,
      polygon: null,
      coordinates: []
    };
  },
  props: {
    paths: {
      type: Array,
      default: () => [],
      description: "Array of coordinates to draw polygon on map"
    },
    resetPaths: {
      type: Boolean,
      default: false,
      description: "Reset polygon on map"
    },
    autoCompleteElementId: {
      type: String,
      default: "autocomplete",
      description: "ID of the autocomplete input element"
    },
    allowPolygonDraw: {
      type: Boolean,
      default: true,
      descriptio: "Allow draw polygon on map"
    },
    location: {
      type: Object,
      default: () => null,
      description: "Object with lat and lng to center the map"
    }
  },
  watch: {
    resetPaths: {
      handler(currentValue, oldValue) {
        if (currentValue) {
          if (this.polygon) {
            this.polygon.setMap(null);
            this.polygon = null;
          }
          this.coordinates = [];
          this.updateCoordinates(this.coordinates);
        }
      },
      deep: true
    },
    paths: {
      handler(currentValue, oldValue) {
        if (currentValue.length > 0) {
          GoogleMapsLoader.load(google => {
            this.loadMap(google);
          });
        }
      },
      deep: true
    },
    location: {
      handler(currentValue, oldValue) {
        if (currentValue && this.map) {
          this.map.setCenter(currentValue);
          this.map.setZoom(12);
        }
      },
      deep: true
    },
    allowPolygonDraw: {
      handler(currentValue, oldValue) {
        if (this.polygon) {
          this.polygon.setEditable(currentValue);
        }
      }
    }
  },
  mounted() {
    // Load Google Maps
    GoogleMapsLoader.load(google => {
      this.loadMap(google);
    });
  },
  methods: {
    /**
     * Resolve map center location
     * @param location object with lat and lng
     * @param paths array of coordinates
     * @param google google maps object
     */
    resolveLocation(location, paths, google) {
      if (location) {
        return location;
      }
      return paths.length > 0 ? this.generatePathCenter(paths, google) : null;
    },
    /**
     * Load Google Map
     *
     */
    loadMap(google) {
      this.google = google;
      this.coordinates = [...this.paths];
      const location = this.resolveLocation(
        this.location,
        this.paths,
        this.google
      );
      this.map = this.generateMap(this.google, location);
      this.loadAutocomplete(this.google, this.map, this.autoCompleteElementId);
      this.handlePolygonDraw(this.google, this.map);
    },
    /**
     * Load autocomplete input
     * @param google google maps object
     * @param map google maps instance
     */
    loadAutocomplete(google, map, autoCompleteElementId = null) {
      const input = document.getElementById(autoCompleteElementId);
      if (!input) {
        console.warn(
          `Autocomplete input element with id ${autoCompleteElementId} not found`
        );
        return;
      }

      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ["geocode"]
      });
      autocomplete.setFields(["address_component", "geometry"]);
      autocomplete.addListener("place_changed", () => {
        if (this.polygon) {
          this.coordinates = [];
          this.polygon.setMap(null);
        }
        const place = autocomplete.getPlace();
        map.setCenter(place.geometry.location);
        map.setZoom(15);
        new google.maps.Marker({
          map: map,
          position: place.geometry.location
        });
      });
    },
    /**
     * Generate google map instance
     * @param google google maps object
     * @param location object with lat and lng
     */
    generateMap(google, location = null) {
      let lat = location ? location.lat : null;
      let lng = location ? location.lng : null;
      let zoom = location ? 11 : 2;

      const mapContainer = document.getElementById("map");
      let myLatlng = new google.maps.LatLng(lat, lng);

      let mapOptions = {
        zoom: zoom,
        scrollwheel: false,
        center: myLatlng,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      };

      return new google.maps.Map(mapContainer, mapOptions);
    },
    /**
     * Create a polygon on the map
     * @param google google maps object
     * @param paths array of coordinates
     * @param editable boolean
     */
    createPolygon(google, paths = [], editable = false) {
      return new google.maps.Polygon({
        paths: paths,
        editable: editable,
        strokeColor: "#FF0000",
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35
      });
    },
    /**
     * Handle polygon draw on map
     * @param google google maps object
     * @param map google maps instance
     */
    handlePolygonDraw(google, map) {
      if (this.coordinates.length > 0) {
        this.polygon = this.createPolygon(
          google,
          this.coordinates,
          this.allowPolygonDraw
        );
        this.polygon.setMap(map);
        this.attachPolygonListeners(google, this.polygon);
      }

      google.maps.event.addListener(map, "click", event => {
        if (!this.allowPolygonDraw) return;

        if (this.polygon) {
          this.polygon.setMap(null);
          this.polygon = null;
        }

        this.coordinates.push({
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        });
        this.updateCoordinates(this.coordinates);

        this.polygon = this.createPolygon(
          google,
          this.coordinates,
          this.allowPolygonDraw
        );
        this.polygon.setMap(map);
        this.attachPolygonListeners(google, this.polygon);
      });
    },
    /**
     * Attach listeners to polygon
     * @param google google maps object
     * @param polygon google maps polygon instance
     */
    attachPolygonListeners(google, polygon) {
      this.polygon.addListener("set_at", () => {
        this.coordinates = this.getCordinates(polygon);
        this.updateCoordinates(this.coordinates);
      });
      polygon.getPaths().forEach((path, index) => {
        google.maps.event.addListener(path, "insert_at", () => {
          this.coordinates = this.getCordinates(polygon);
          this.updateCoordinates(this.coordinates);
        });

        google.maps.event.addListener(path, "remove_at", () => {
          this.coordinates = this.getCordinates(polygon);
          this.updateCoordinates(this.coordinates);
        });

        google.maps.event.addListener(path, "set_at", () => {
          this.coordinates = this.getCordinates(polygon);
          this.updateCoordinates(this.coordinates);
        });
      });
    },
    /**
     * Emit updated coordinates
     * @param coordinates array of coordinates
     */
    updateCoordinates(coordinates = []) {
      this.$emit("updateCoordinates", coordinates);
    },
    /**
     * Generate center point from polygon path
     * @param polygonPath array of coordinates
     * @param google google maps object
     */
    generatePathCenter(polygonPath, google) {
      var bounds = new google.maps.LatLngBounds();
      let coordinates = polygonPath.map(
        point => new google.maps.LatLng(point.lat, point.lng)
      );
      coordinates.forEach(element => {
        bounds.extend(element);
      });
      const center = bounds.getCenter();
      return { lat: center.lat(), lng: center.lng() };
    },
    /**
     * Get coordinates from polygon
     * @param polygon google maps polygon instance
     */
    getCordinates(polygon) {
      if (!polygon) return [];

      var path = polygon.getPath();
      return path.getArray().map(latlng => {
        return { lat: latlng.lat(), lng: latlng.lng() };
      });
    }
  }
};
</script>
